import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  MapPinIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  PhoneIcon,
  PlusIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const today = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};
const emptyGarage = {
  Nom: '',
  AdresseLigne1: '',
  AdresseLigne2: '',
  CodePostal: '',
  Ville: '',
  Pays: 'FR',
  Telephone: '',
  Email: '',
  Siret: '',
  RaisonSociale: '',
};

const formatDate = (value) => value
  ? new Intl.DateTimeFormat('fr-FR').format(new Date(value))
  : 'Durée indéterminée';

function StatusMessage({ message }) {
  if (!message?.text) return null;
  return (
    <p className={`rounded-lg border px-4 py-3 text-sm font-semibold ${message.type === 'error' ? 'border-rose-500/30 bg-rose-500/10 text-rose-100' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'}`}>
      {message.text}
    </p>
  );
}

function Field({ label, required = false, children }) {
  return (
    <label className="block">
      <span className="text-sm font-bold text-slate-300">{label}{required ? ' *' : ''}</span>
      {children}
    </label>
  );
}

const fieldClass = 'mt-2 w-full rounded-lg border border-sky-500/20 bg-slate-950/65 px-3 py-2.5 text-slate-100 outline-none placeholder:text-slate-600 focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20';

export default function GaragePage() {
  const [garages, setGarages] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [canCreateImmobilisation, setCanCreateImmobilisation] = useState(false);
  const [isSupplementary, setIsSupplementary] = useState(false);
  const [writeEnabled, setWriteEnabled] = useState(true);
  const [immobilisations, setImmobilisations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [garageForm, setGarageForm] = useState(emptyGarage);
  const [editingGarageId, setEditingGarageId] = useState(null);
  const [savingGarage, setSavingGarage] = useState(false);
  const [vehicleId, setVehicleId] = useState('');
  const [immobilisationGarageMode, setImmobilisationGarageMode] = useState('saved');
  const [immobilisationGarageId, setImmobilisationGarageId] = useState('');
  const [manualGarageName, setManualGarageName] = useState('');
  const [startDate, setStartDate] = useState(today());
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [creatingImmobilisation, setCreatingImmobilisation] = useState(false);
  const [closeDates, setCloseDates] = useState({});
  const [closingId, setClosingId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [garageResponse, immobilisationResponse, userResponse] = await Promise.all([
        api.get('/garages'),
        api.get('/garages/immobilisations'),
        api.get('/users/me'),
      ]);
      const nextGarages = garageResponse.data?.garages || [];
      const nextVehicles = immobilisationResponse.data?.vehicles || [];
      const accountStatus = userResponse.data || {};
      const accountCanWrite = Boolean(accountStatus.EcritureAutorisee);
      const gradeCanCreateMaintenance = Number(accountStatus.Droits?.EntretienCreate || 0) > 0;
      const accountIsSupplementary = Boolean(accountStatus.EstCompteSupplementaire);
      const calculatedCanCreate = accountCanWrite && gradeCanCreateMaintenance;
      const backendCanCreate = immobilisationResponse.data?.PeutCreer;
      setGarages(nextGarages);
      setCanManage(Boolean(garageResponse.data?.PeutGerer) && calculatedCanCreate && !accountIsSupplementary);
      setCanCreateImmobilisation(backendCanCreate === undefined
        ? calculatedCanCreate
        : calculatedCanCreate && Boolean(backendCanCreate));
      setIsSupplementary(accountIsSupplementary);
      setWriteEnabled(accountCanWrite);
      setImmobilisations(immobilisationResponse.data?.immobilisations || []);
      setVehicles(nextVehicles);
      setVehicleId((current) => current || String(nextVehicles[0]?.VehiculeID || ''));
      const firstActiveGarage = nextGarages.find((garage) => garage.EstActif);
      setImmobilisationGarageId((current) => current || String(firstActiveGarage?.GarageID || ''));
      if (!firstActiveGarage) setImmobilisationGarageMode('manual');
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Impossible de charger les garages.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const activeGarages = useMemo(() => garages.filter((garage) => garage.EstActif), [garages]);
  const activeImmobilisations = useMemo(() => immobilisations.filter((item) => item.EstActive), [immobilisations]);
  const pastImmobilisations = useMemo(() => immobilisations.filter((item) => !item.EstActive), [immobilisations]);
  const unavailableVehicleIds = useMemo(() => new Set(activeImmobilisations.map((item) => item.VehiculeID)), [activeImmobilisations]);
  const availableVehicles = useMemo(() => vehicles.filter((vehicle) => !unavailableVehicleIds.has(vehicle.VehiculeID)), [vehicles, unavailableVehicleIds]);

  useEffect(() => {
    if (!availableVehicles.some((vehicle) => String(vehicle.VehiculeID) === String(vehicleId))) {
      setVehicleId(String(availableVehicles[0]?.VehiculeID || ''));
    }
  }, [availableVehicles, vehicleId]);

  const updateGarageField = (name, value) => setGarageForm((current) => ({ ...current, [name]: value }));

  const resetGarageForm = () => {
    setEditingGarageId(null);
    setGarageForm(emptyGarage);
  };

  const handleGarageSubmit = async (event) => {
    event.preventDefault();
    setSavingGarage(true);
    setMessage(null);
    try {
      const response = editingGarageId
        ? await api.put(`/garages/${editingGarageId}`, garageForm)
        : await api.post('/garages', garageForm);
      setMessage({ type: 'success', text: response.data?.message || 'Garage enregistré.' });
      resetGarageForm();
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Impossible d’enregistrer le garage.' });
    } finally {
      setSavingGarage(false);
    }
  };

  const editGarage = (garage) => {
    setEditingGarageId(garage.GarageID);
    setGarageForm(Object.fromEntries(Object.keys(emptyGarage).map((key) => [key, garage[key] || (key === 'Pays' ? 'FR' : '')])));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const changeGarageState = async (garage) => {
    setMessage(null);
    try {
      const response = await api.put(`/garages/${garage.GarageID}/state`, { EstActif: !garage.EstActif });
      setMessage({ type: 'success', text: response.data?.message });
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Impossible de modifier le garage.' });
    }
  };

  const createImmobilisation = async (event) => {
    event.preventDefault();
    setCreatingImmobilisation(true);
    setMessage(null);
    try {
      const response = await api.post('/garages/immobilisations', {
        VehiculeID: vehicleId,
        GarageID: immobilisationGarageMode === 'saved' ? immobilisationGarageId : null,
        GarageNom: immobilisationGarageMode === 'manual' ? manualGarageName : null,
        DateDebut: startDate,
        DateFin: plannedEndDate || null,
      });
      setMessage({ type: 'success', text: response.data?.message });
      setManualGarageName('');
      setStartDate(today());
      setPlannedEndDate('');
      await loadData();
      window.dispatchEvent(new Event('vehicle:immobilisations-changed'));
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Impossible de déclarer cette immobilisation.' });
    } finally {
      setCreatingImmobilisation(false);
    }
  };

  const closeImmobilisation = async (item) => {
    setClosingId(item.ImmobilisationVehiculeID);
    setMessage(null);
    try {
      const response = await api.put(`/garages/immobilisations/${item.ImmobilisationVehiculeID}/close`, {
        DateFin: closeDates[item.ImmobilisationVehiculeID] || today(),
      });
      setMessage({ type: 'success', text: response.data?.message });
      await loadData();
      window.dispatchEvent(new Event('vehicle:immobilisations-changed'));
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Impossible de clôturer cette immobilisation.' });
    } finally {
      setClosingId(null);
    }
  };

  return (
    <main className="mx-auto max-w-7xl space-y-6 text-slate-100">
      <header className="rounded-xl border border-sky-500/20 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.18),transparent_35%),linear-gradient(135deg,rgba(2,6,23,0.98),rgba(15,23,42,0.92))] p-6 shadow-2xl shadow-sky-950/25">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-sky-300">Carnet d’adresses</p>
            <h2 className="mt-2 text-3xl font-black text-white">Garages et immobilisations</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-400">Enregistrez vos garages habituels, réutilisez-les dans les entretiens et signalez les véhicules temporairement indisponibles.</p>
          </div>
          <BuildingStorefrontIcon className="size-14 text-sky-300" />
        </div>
      </header>

      <StatusMessage message={message} />

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-xl border border-sky-500/20 bg-slate-950/60 py-16 text-slate-400"><ArrowPathIcon className="size-6 animate-spin" /> Chargement…</div>
      ) : (
        <>
          <section className="grid gap-6 xl:grid-cols-[minmax(340px,0.42fr)_minmax(0,1fr)]">
            <div className="rounded-xl border border-sky-500/20 bg-slate-950/65 p-5">
              <div className="flex items-center gap-3"><PlusIcon className="size-6 text-sky-300" /><h3 className="text-xl font-black text-white">{editingGarageId ? 'Modifier le garage' : 'Ajouter un garage'}</h3></div>
              {canManage ? (
                <form onSubmit={handleGarageSubmit} className="mt-5 space-y-4">
                  <Field label="Nom" required><input className={fieldClass} value={garageForm.Nom} onChange={(event) => updateGarageField('Nom', event.target.value)} maxLength={150} required /></Field>
                  <Field label="Adresse" required><input className={fieldClass} value={garageForm.AdresseLigne1} onChange={(event) => updateGarageField('AdresseLigne1', event.target.value)} maxLength={180} required /></Field>
                  <Field label="Complément d’adresse"><input className={fieldClass} value={garageForm.AdresseLigne2} onChange={(event) => updateGarageField('AdresseLigne2', event.target.value)} maxLength={180} /></Field>
                  <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3">
                    <Field label="Code postal" required><input className={fieldClass} value={garageForm.CodePostal} onChange={(event) => updateGarageField('CodePostal', event.target.value)} maxLength={20} required /></Field>
                    <Field label="Ville" required><input className={fieldClass} value={garageForm.Ville} onChange={(event) => updateGarageField('Ville', event.target.value)} maxLength={100} required /></Field>
                  </div>
                  <Field label="Téléphone" required><input type="tel" className={fieldClass} value={garageForm.Telephone} onChange={(event) => updateGarageField('Telephone', event.target.value)} maxLength={30} required /></Field>
                  <Field label="Email"><input type="email" className={fieldClass} value={garageForm.Email} onChange={(event) => updateGarageField('Email', event.target.value)} maxLength={100} /></Field>
                  <Field label="Raison sociale"><input className={fieldClass} value={garageForm.RaisonSociale} onChange={(event) => updateGarageField('RaisonSociale', event.target.value)} maxLength={150} /></Field>
                  <Field label="SIRET"><input inputMode="numeric" className={fieldClass} value={garageForm.Siret} onChange={(event) => updateGarageField('Siret', event.target.value.replace(/\D/g, '').slice(0, 14))} maxLength={14} /></Field>
                  <div className="flex gap-3 pt-2">
                    <button disabled={savingGarage} className="rounded-lg bg-sky-500 px-4 py-2.5 text-sm font-black text-white hover:bg-sky-400 disabled:opacity-50">{savingGarage ? 'Enregistrement…' : editingGarageId ? 'Mettre à jour' : 'Ajouter'}</button>
                    {editingGarageId && <button type="button" onClick={resetGarageForm} className="rounded-lg border border-slate-600 px-4 py-2.5 text-sm font-bold text-slate-300 hover:bg-white/5">Annuler</button>}
                  </div>
                </form>
              ) : (
                <p className="mt-5 rounded-lg border border-amber-500/20 bg-amber-500/10 p-4 text-sm leading-6 text-amber-100">
                  {!writeEnabled
                    ? 'La période d’essai ou l’abonnement du compte est expiré. Les garages restent consultables en lecture seule.'
                    : isSupplementary
                      ? 'Les garages sont partagés avec votre organisation. Seul le compte professionnel principal peut créer ou modifier leurs fiches.'
                      : 'Votre grade ne dispose pas du droit nécessaire pour créer ou modifier les fiches garage.'}
                </p>
              )}
            </div>

            <div className="space-y-4">
              <div><h3 className="text-xl font-black text-white">Garages enregistrés</h3><p className="mt-1 text-sm text-slate-500">{activeGarages.length} fiche{activeGarages.length > 1 ? 's' : ''} active{activeGarages.length > 1 ? 's' : ''}</p></div>
              {garages.length ? garages.map((garage) => (
                <article key={garage.GarageID} className={`rounded-xl border p-5 ${garage.EstActif ? 'border-sky-500/20 bg-slate-950/65' : 'border-slate-700 bg-slate-950/35 opacity-70'}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2"><h4 className="text-lg font-black text-white">{garage.Nom}</h4><span className={`rounded-full px-2.5 py-1 text-xs font-black ${garage.EstActif ? 'bg-emerald-500/15 text-emerald-200' : 'bg-slate-700 text-slate-300'}`}>{garage.EstActif ? 'Actif' : 'Désactivé'}</span></div>
                      {garage.RaisonSociale && <p className="mt-1 text-sm text-slate-400">{garage.RaisonSociale}</p>}
                      <div className="mt-4 grid gap-2 text-sm text-slate-300 md:grid-cols-2">
                        <p className="flex gap-2"><MapPinIcon className="mt-0.5 size-4 shrink-0 text-sky-300" /><span>{garage.AdresseLigne1}{garage.AdresseLigne2 ? `, ${garage.AdresseLigne2}` : ''}<br />{garage.CodePostal} {garage.Ville}</span></p>
                        <div className="space-y-2"><p className="flex items-center gap-2"><PhoneIcon className="size-4 text-sky-300" />{garage.Telephone}</p>{garage.Email && <p className="flex items-center gap-2"><EnvelopeIcon className="size-4 text-sky-300" />{garage.Email}</p>}{garage.Siret && <p className="text-xs text-slate-500">SIRET : {garage.Siret}</p>}</div>
                      </div>
                    </div>
                    {canManage && <div className="flex gap-2"><button onClick={() => editGarage(garage)} className="rounded-lg border border-sky-500/20 p-2 text-sky-200 hover:bg-sky-500/10" title="Modifier"><PencilSquareIcon className="size-5" /></button><button onClick={() => changeGarageState(garage)} className="rounded-lg border border-slate-600 p-2 text-slate-300 hover:bg-white/5" title={garage.EstActif ? 'Désactiver' : 'Réactiver'}>{garage.EstActif ? <NoSymbolIcon className="size-5" /> : <CheckCircleIcon className="size-5" />}</button></div>}
                  </div>
                </article>
              )) : <div className="rounded-xl border border-dashed border-sky-500/25 p-10 text-center text-slate-500">Aucun garage enregistré. La saisie libre reste disponible dans les entretiens.</div>}
            </div>
          </section>

          <section className="rounded-xl border border-amber-500/20 bg-[linear-gradient(135deg,rgba(69,26,3,0.28),rgba(2,6,23,0.92))] p-6">
            <div className="flex items-center gap-3"><WrenchScrewdriverIcon className="size-7 text-amber-300" /><div><h3 className="text-xl font-black text-white">Signaler un véhicule indisponible</h3><p className="mt-1 text-sm text-slate-400">Le véhicule reste accessible et modifiable. Le signalement est partagé avec tous les membres du compte.</p></div></div>
            {!canCreateImmobilisation && (
              <p className="mt-5 rounded-lg border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-100">
                {writeEnabled ? 'Votre grade ne permet pas de créer une immobilisation.' : 'Les créations sont bloquées tant que l’abonnement ou la période d’essai n’est pas actif.'}
              </p>
            )}
            <form onSubmit={createImmobilisation} className={`mt-5 grid gap-4 lg:grid-cols-2 xl:grid-cols-4 ${canCreateImmobilisation ? '' : 'opacity-50'}`}>
              <fieldset disabled={!canCreateImmobilisation} className="contents">
              <Field label="Véhicule" required><select className={fieldClass} value={vehicleId} onChange={(event) => setVehicleId(event.target.value)} required><option value="">Sélectionner…</option>{availableVehicles.map((vehicle) => <option key={vehicle.VehiculeID} value={vehicle.VehiculeID}>{vehicle.Nom}{vehicle.Modele ? ` · ${vehicle.Modele}` : ''}</option>)}</select></Field>
              <div>
                <span className="text-sm font-bold text-slate-300">Garage</span>
                <div className="mt-2 flex gap-2 text-xs font-bold"><button type="button" disabled={!activeGarages.length} onClick={() => setImmobilisationGarageMode('saved')} className={`rounded-md px-3 py-1.5 ${immobilisationGarageMode === 'saved' ? 'bg-sky-500/20 text-sky-100' : 'border border-slate-700 text-slate-400'} disabled:opacity-40`}>Enregistré</button><button type="button" onClick={() => setImmobilisationGarageMode('manual')} className={`rounded-md px-3 py-1.5 ${immobilisationGarageMode === 'manual' ? 'bg-sky-500/20 text-sky-100' : 'border border-slate-700 text-slate-400'}`}>Saisie libre</button></div>
                {immobilisationGarageMode === 'saved' ? <select className={fieldClass} value={immobilisationGarageId} onChange={(event) => setImmobilisationGarageId(event.target.value)}><option value="">Sans garage</option>{activeGarages.map((garage) => <option key={garage.GarageID} value={garage.GarageID}>{garage.Nom}</option>)}</select> : <input className={fieldClass} maxLength={150} value={manualGarageName} onChange={(event) => setManualGarageName(event.target.value)} placeholder="Nom du garage (optionnel)" />}
              </div>
              <Field label="Début" required><input type="date" className={fieldClass} value={startDate} onChange={(event) => setStartDate(event.target.value)} required /></Field>
              <Field label="Fin prévue (optionnelle)"><input type="date" className={fieldClass} min={startDate} value={plannedEndDate} onChange={(event) => setPlannedEndDate(event.target.value)} /><span className="mt-2 block text-xs text-slate-500">Laissez vide si la durée est indéterminée.</span></Field>
              <div className="lg:col-span-2 xl:col-span-4"><button disabled={!canCreateImmobilisation || creatingImmobilisation || !vehicleId} className="rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-black text-slate-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50">{creatingImmobilisation ? 'Signalement…' : 'Déclarer indisponible'}</button></div>
              </fieldset>
            </form>
          </section>

          <section className="space-y-4">
            <div><h3 className="text-xl font-black text-white">Immobilisations actives</h3><p className="mt-1 text-sm text-slate-500">{activeImmobilisations.length} véhicule{activeImmobilisations.length > 1 ? 's' : ''} actuellement indisponible{activeImmobilisations.length > 1 ? 's' : ''}</p></div>
            {activeImmobilisations.length ? activeImmobilisations.map((item) => (
              <article key={item.ImmobilisationVehiculeID} className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-5">
                <div className="flex flex-wrap items-start justify-between gap-5">
                  <div><p className="flex items-center gap-2 text-lg font-black text-white"><TruckIcon className="size-5 text-amber-300" />{item.Vehicule?.Nom}</p><p className="mt-2 text-sm text-slate-300">{item.Garage?.Nom || item.GarageNom || 'Garage non renseigné'}</p><p className="mt-2 flex items-center gap-2 text-sm text-slate-400"><CalendarDaysIcon className="size-4" />Depuis le {formatDate(item.DateDebut)} · {item.DateFin ? `fin prévue le ${formatDate(item.DateFin)}` : 'durée indéterminée'}</p><p className="mt-2 text-xs text-slate-500">Signalé par {item.Createur?.Surnom || 'un membre du compte'}</p></div>
                  {item.PeutCloturer && <div className="flex flex-wrap items-end gap-2"><Field label="Date de remise en service"><input type="date" min={String(item.DateDebut).slice(0, 10)} className={fieldClass} value={closeDates[item.ImmobilisationVehiculeID] || today()} onChange={(event) => setCloseDates((current) => ({ ...current, [item.ImmobilisationVehiculeID]: event.target.value }))} /></Field><button type="button" onClick={() => closeImmobilisation(item)} disabled={closingId === item.ImmobilisationVehiculeID} className="mb-0.5 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-black text-slate-950 hover:bg-emerald-400 disabled:opacity-50">Remettre en service</button></div>}
                </div>
              </article>
            )) : <div className="rounded-xl border border-dashed border-emerald-500/25 p-8 text-center text-emerald-200"><CheckCircleIcon className="mx-auto mb-3 size-8" />Tous les véhicules sont disponibles.</div>}
          </section>

          {pastImmobilisations.length > 0 && <section><h3 className="text-xl font-black text-white">Historique des immobilisations</h3><div className="mt-4 overflow-hidden rounded-xl border border-sky-500/20"><div className="divide-y divide-white/10">{pastImmobilisations.map((item) => <article key={item.ImmobilisationVehiculeID} className="grid gap-2 bg-slate-950/55 px-5 py-4 text-sm md:grid-cols-[minmax(180px,1fr)_minmax(180px,1fr)_220px]"><p className="font-bold text-white">{item.Vehicule?.Nom}</p><p className="text-slate-400">{item.Garage?.Nom || item.GarageNom || 'Garage non renseigné'}</p><p className="text-slate-500">Du {formatDate(item.DateDebut)} au {formatDate(item.DateFin)}</p></article>)}</div></div></section>}
        </>
      )}
    </main>
  );
}
